import {memo, forwardRef} from 'react';
import {
  CustomContentProps,
  SnackbarContent,
  VariantType,
  useSnackbar,
} from 'notistack';
import {IconButton, Typography, Box, useTheme} from '@mui/material';
import {
  CheckCircle,
  Error,
  ReportProblem,
  Info,
  Close,
} from '@mui/icons-material';
import {tokens} from '../../theme';

const variantIconMap: Record<VariantType, React.ElementType | null> = {
  default: null,
  success: CheckCircle,
  error: Error,
  warning: ReportProblem,
  info: Info,
};

const variantColorMap: Record<VariantType, string | undefined> = {
  default: undefined,
  success: 'success',
  error: 'error',
  warning: 'warning',
  info: 'info',
};

const SnackbarInner = forwardRef<HTMLDivElement, CustomContentProps>(
  (props, forwardedRef) => {
    const {palette} = useTheme();
    const {message, variant, action} = props;
    const {closeSnackbar} = useSnackbar();

    const customAction =
      typeof action === 'function' ? action(props.id) : action;

    const Icon = variantIconMap[variant];

    const variantBorderColor =
      variant === 'default'
        ? palette.common.black
        : palette[variant as 'success' | 'error' | 'warning' | 'info']?.main;

    return (
      <SnackbarContent
        ref={forwardedRef}
        style={{
          backgroundColor: palette.background.default,
          alignItems: 'center',
          padding: '6px 16px',
          borderRadius: `${tokens.radius.sm}px`,
          boxShadow: tokens.shadow.md,
          justifyContent: 'space-between',
          border: `1px solid ${variantBorderColor}`,
        }}
      >
        {Icon && (
          <Icon
            color={variantColorMap[variant] as any}
            sx={{fontSize: 20, opacity: 0.9, mr: 1}}
          />
        )}
        <Typography
          variant="body1"
          sx={{display: 'flex', alignItems: 'center', py: 1}}
        >
          {message}
        </Typography>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          {customAction && customAction}
          {!customAction && (
            <IconButton onClick={() => closeSnackbar(props.id)}>
              <Close fontSize="small" />
            </IconButton>
          )}
        </Box>
      </SnackbarContent>
    );
  },
);

export const Snackbar = memo(SnackbarInner);
